import React from 'react';
import { useTranslation } from 'react-i18next';

import { PaymentDto, PaymentVopMatchStatus } from '@alpha/payments-dtos';
import { TKeyValue } from '@alpha/ui-lib/ui/table';
import {
  faCheck as faCheckRegular,
  faCircleXmark,
  faMinusCircle as faMinusCircleRegular,
} from '@fortawesome/pro-regular-svg-icons';
import {
  faShieldCheck,
  faShieldExclamation,
  faShieldSlash,
  faShieldTimes,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import mapVOPStatusToDisplay from '../mapVOPStatusToDisplay';

const useVOPConfirmation = (payment?: PaymentDto) => {
  const { t } = useTranslation();
  const vop = payment?.vopDetails;

  const getShieldColor = (status?: PaymentVopMatchStatus) => {
    switch (status) {
      case PaymentVopMatchStatus.MATCH:
        return '#1E8777';
      case PaymentVopMatchStatus.NEAR_MATCH:
        return '#CF752F';
      case PaymentVopMatchStatus.NO_MATCH:
        return '#A62B23';
      case PaymentVopMatchStatus.FAILED:
      case PaymentVopMatchStatus.NOT_AVAILABLE:
        return '#1B1B1B40';
      default:
        return '#1B1B1B40';
    }
  };

  const getMessage = (status?: PaymentVopMatchStatus) => {
    switch (status) {
      case PaymentVopMatchStatus.MATCH:
        return 'vop_Match_message';
      case PaymentVopMatchStatus.NO_MATCH:
        return 'vop_NoMatch_message';
      case PaymentVopMatchStatus.NEAR_MATCH:
        return 'vop_NearMatch_message';
      case PaymentVopMatchStatus.FAILED:
      case PaymentVopMatchStatus.NOT_AVAILABLE:
        return 'vop_NotAvailable_message';
      default:
        return 'vop_NotAvailable_message';
    }
  };

  const getIcon = (status?: PaymentVopMatchStatus) => {
    switch (status) {
      case PaymentVopMatchStatus.MATCH:
        return faShieldCheck;
      case PaymentVopMatchStatus.NEAR_MATCH:
        return faShieldExclamation;
      case PaymentVopMatchStatus.NO_MATCH:
        return faShieldTimes;
      case PaymentVopMatchStatus.FAILED:
      case PaymentVopMatchStatus.NOT_AVAILABLE:
        return faShieldSlash;
      default:
        return faShieldSlash;
    }
  };

  const matchedIcon = (matched: boolean | undefined, status?: PaymentVopMatchStatus) => {
    if (status === PaymentVopMatchStatus.NO_MATCH) return <FontAwesomeIcon icon={faCircleXmark} color="#A62B23" style={{ marginLeft: '16px' }} />;
    return matched
      ? <FontAwesomeIcon icon={faCheckRegular} color="#51A869" style={{ marginLeft: '16px' }} />
      : <FontAwesomeIcon icon={faMinusCircleRegular} color="#c4c4c4" style={{ marginLeft: '16px' }} />;
  };

  const getCloseMatchTable = () => {
    const closeMatchDetail: TKeyValue[] = [];

    if (vop?.status !== PaymentVopMatchStatus.NEAR_MATCH) {
      return closeMatchDetail;
    }

    if (vop?.name && payment?.beneficiaryName) {
      closeMatchDetail.push({
        key: t('Name Entered'),
        value: payment?.beneficiaryName,
      });
      closeMatchDetail.push({
        key: t('Name Found'),
        value: <b>{vop.name}</b>,
      });
    }

    return closeMatchDetail;
  };

  const getMatchedTable = () => {
    const matchedDetail: TKeyValue[] = [];
    const matched = vop?.status === PaymentVopMatchStatus.MATCH || vop?.status === PaymentVopMatchStatus.NEAR_MATCH;

    if (vop?.status !== PaymentVopMatchStatus.NEAR_MATCH) {
      matchedDetail.push({
        key: t('name'),
        value: (
          <>
            {payment?.beneficiaryName}
            {matchedIcon(matched, vop?.status)}
          </>
        ),
      });
    }

    matchedDetail.push({
      key: t('IBAN'),
      value: (
        <>
          {payment?.beneficiaryIban}
          {matchedIcon(matched, vop?.status)}
        </>
      ),
      datadogMask: true,
    });

    matchedDetail.push({
      key: t('SWIFT / BIC'),
      value: (
        <>
          {payment?.beneficiarySwift}
          {matchedIcon(matched, vop?.status)}
        </>
      ),
      datadogMask: true,
    });

    return matchedDetail;
  };

  return {
    icon: getIcon(vop?.status),
    title: t(mapVOPStatusToDisplay(vop?.status)),
    message: getMessage(vop?.status),
    color: getShieldColor(vop?.status),
    closeMatchTable: getCloseMatchTable(),
    matchedTable: getMatchedTable(),
  };
};

export default useVOPConfirmation;
